const JSON_LD_ATTR = 'data-seo-id';

/**
 * Inject or update a JSON-LD script block in <head>.
 * Uses data-seo-id to identify and deduplicate blocks.
 */
export function injectJsonLd(id: string, data: object | object[]): void {
  let script = document.querySelector<HTMLScriptElement>(
    `script[${JSON_LD_ATTR}="${id}"]`,
  );
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(JSON_LD_ATTR, id);
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

/**
 * Remove a JSON-LD script block by its seo-id.
 */
export function removeJsonLd(id: string): void {
  const script = document.querySelector<HTMLScriptElement>(
    `script[${JSON_LD_ATTR}="${id}"]`,
  );
  script?.remove();
}

/**
 * Remove all dynamically injected JSON-LD blocks.
 */
export function removeAllJsonLd(): void {
  document
    .querySelectorAll<HTMLScriptElement>(`script[${JSON_LD_ATTR}]`)
    .forEach((el) => el.remove());
}

// ---------- Builder helpers ----------

/** Strip undefined/null values from an object so JSON output stays clean. */
function clean<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  ) as Partial<T>;
}

export interface LocalBusinessData {
  name: string;
  url: string;
  description?: string;
  telephone?: string;
  email?: string;
  image?: string;
  streetAddress?: string;
  sameAs?: string[];
  aggregateRating?: object;
  review?: object[];
  /** Schema.org type override (default: 'LocalBusiness') */
  businessType?: string;
}

export function buildLocalBusinessLd(data: LocalBusinessData): object {
  return clean({
    '@context': 'https://schema.org',
    '@type': data.businessType || 'LocalBusiness',
    '@id': `${data.url}/#business`,
    name: data.name,
    description: data.description,
    url: data.url,
    telephone: data.telephone,
    email: data.email,
    image: data.image,
    address: data.streetAddress
      ? {
          '@type': 'PostalAddress',
          streetAddress: data.streetAddress,
          addressCountry: 'PL',
        }
      : undefined,
    sameAs:
      data.sameAs && data.sameAs.length > 0 ? data.sameAs : undefined,
    potentialAction: {
      '@type': 'ReserveAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: data.url,
        actionPlatform: [
          'http://schema.org/DesktopWebPlatform',
          'http://schema.org/MobileWebPlatform',
        ],
      },
      result: { '@type': 'Reservation' },
    },
    aggregateRating: data.aggregateRating,
    review: data.review && data.review.length > 0 ? data.review : undefined,
  });
}

export interface AggregateRatingData {
  ratingValue: number;
  ratingCount: number;
}

export function buildAggregateRatingLd(data: AggregateRatingData): object {
  return {
    '@type': 'AggregateRating',
    ratingValue: data.ratingValue,
    bestRating: 5,
    worstRating: 1,
    ratingCount: data.ratingCount,
  };
}

export interface ReviewData {
  authorName: string;
  ratingValue: number;
  reviewBody: string;
  datePublished: string;
}

export function buildReviewLd(reviews: ReviewData[]): object[] {
  return reviews.map((r) => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: r.authorName },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: r.ratingValue,
      bestRating: 5,
    },
    reviewBody: r.reviewBody,
    datePublished: r.datePublished,
  }));
}

export interface WebSiteData {
  name: string;
  url: string;
  publisherId?: string;
}

export function buildWebSiteLd(data: WebSiteData): object {
  return clean({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${data.url}/#website`,
    name: data.name,
    url: data.url,
    inLanguage: 'pl-PL',
    publisher: data.publisherId
      ? { '@id': data.publisherId }
      : undefined,
  });
}

export interface WebSiteSearchData {
  name: string;
  url: string;
  searchUrlTemplate: string;
}

export function buildWebSiteWithSearchLd(data: WebSiteSearchData): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: data.name,
    url: data.url,
    inLanguage: 'pl-PL',
    potentialAction: {
      '@type': 'SearchAction',
      target: data.searchUrlTemplate,
      'query-input': 'required name=search_term_string',
    },
  };
}
